import {
  StandardDataEnumDimension,
  StandardDataProperty,
  StandardDataUnit,
} from "@/lib/api";

import { Dimension, DimensionValue } from ".";

export class EnumDimensionValue implements DimensionValue {
  public content: string;

  constructor(content: string) {
    this.content = content;
  }

  get queryStringValue() {
    return this.content;
  }
}

export type EnumDimensionProps = {
  id: string;
  values: EnumDimensionValue[];
  default: EnumDimensionValue;
  property: StandardDataProperty;
  unit?: StandardDataUnit;
};

export class EnumDimension implements Dimension {
  private props;

  constructor(props: EnumDimensionProps) {
    this.props = props;
  }

  static fromData(data: StandardDataEnumDimension) {
    return new EnumDimension({
      id: data.id,
      values: data.values.map((value) => new EnumDimensionValue(value)),
      default: new EnumDimensionValue(data.default),
      property: data.property,
      unit: data.unit,
    });
  }

  get id() {
    return this.props.id;
  }
  get values() {
    return this.props.values;
  }
  get default() {
    return this.props.default;
  }

  get property() {
    return this.props.property;
  }
  get unit() {
    return this.props.unit;
  }

  contains(value: DimensionValue) {
    if (!(value instanceof EnumDimensionValue)) return false;
    return this.values.map((value) => value.content).includes(value.content);
  }

  snap(value: DimensionValue) {
    return this.contains(value) ? value : undefined;
  }

  merge(other: Dimension) {
    if (!(other instanceof EnumDimension))
      throw new Error("attempted to merge dimensions of different types");
    if (other.id !== this.id)
      throw new Error("attempted to merge dimensions with different IDs");
    if (other.property.id !== this.property.id)
      throw new Error("attempted to merge Enum dimensions with different property IDs");
    if (other.unit?.type !== this.unit?.type)
      throw new Error("attempted to merge Enum dimensions with different unit types");

    const commonDefault = [this.default, other.default].find(
      (value) => this.contains(value) && other.contains(value),
    );

    return new EnumDimension({
      ...this.props,
      values: Array.from(
        new Set([
          ...this.values.map((v) => v.content),
          ...other.values.map((v) => v.content),
        ]),
      ).map((s) => new EnumDimensionValue(s)),
      default: commonDefault || this.default,
    });
  }
}
